import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useMemo, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {NameTemplateType} from 'name-templates/NameTemplateConstants';
import notifications from 'notifications';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import SchemaTagInputsWithDefault from 'schemas/tags/SchemaTagInputsWithDefault';
import {navigateToStudyOverviewTab, refreshStudyFolder} from 'studies/FileUtils';
import useTimingEstimate from 'studies/progress/useTimingEstimate';
import {STUDY_UNKNOWN_ERROR_ACTION, onOpenStudyAfterCreate} from 'studies/sidepanel/StudyCreationTrayTask';
import ChangeStudyableAssignmentPermissionWarningBanner, {
  ChangeStudyableAssignmentPermissionOriginType,
} from 'studies/studyable/ChangeStudyableAssignmentPermissionWarningBanner';
import {
  AUTOGENERATED_NAME_TEXT,
  EMPTY_PREFIX,
  STUDY_DESCRIPTION_MAX_LENGTH,
  STUDY_DISPLAY_ID_MAX_LENGTH,
} from 'studies/StudyConstants';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import {StudySchema} from 'studies/typed/utils/TypedStudyTypes';
import {StudySchemaEntryTemplatesQuery} from 'studies/typed/utils/ui/__generated/StudySchemaEntryTemplatesQuery';
import {DraftStudySchema, getStudyMaxNameLength, useDraftStudy} from 'studies/typed/utils/Validation';
import getTestProps from 'test/lib/cypress/getTestProps';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {InputErrorMessageStyle, LabelStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal from 'ui-library/modals/Modal';
import StepNav, {StepNavVariant} from 'ui-library/StepNav';
import {assertNever} from 'util/Types';

import {BulkCreateStudiesParam} from '../StudyDAO';
import {shouldAllowSubmit} from '../typed/utils/ui/StudyModalUtils';
import {CreateModalStudyQuery} from './__generated/CreateModalStudyQuery';
import AddProcedureRunModal from './AddProcedureRunModal';
import AddProcedureRunPlanModal from './AddProcedureRunPlanModal';
import {CreateStudyModalOptionalParams} from './NewStudyDropdown';
import {onCreateStudy} from './onCreateStudy';

type StudySchemaTemplatesQuery = ExtractByTypename<
  StudySchemaEntryTemplatesQuery['studySchema'],
  'StudySchemaNode'
>['entryTemplates'][number];

export type TemplateNode = ExtractByTypename<StudySchemaTemplatesQuery, 'TemplateNode'>;

export interface CreateStudyModalProps {
  onRequestClose: () => void;
  onRequestSubmit?: () => void;
  studySchemaId: string;
  actionOrigin: ActionOrigin;
  studySchema: StudySchema;
  initialValues: CreateStudyModalOptionalParams;
}

export interface QueryingCreateStudyModalProps {
  onRequestClose: () => void;
  onRequestSubmit?: () => void;
  studySchemaId: string;
  actionOrigin: ActionOrigin;
  initialValues: CreateStudyModalOptionalParams;
}

/**
 * Exported for testing
 */
export const CREATE_MODAL_STUDIES_QUERY: DocumentNode = gql`
  query CreateModalStudyQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    studySchema(id: $id) {
      ... on StudySchemaNode {
        id
        name
        nameTemplates {
          ... on NameTemplateNode {
            templateType
            nameTemplateParts {
              ... on NameTemplatePartNode {
                type
              }
            }
          }
        }
        fieldsJson
        studyType
        prefix
        entryTemplates {
          ... on TemplateNode {
            id
            name
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function QueryingCreateStudyModal(props: QueryingCreateStudyModalProps): JSX.Element | null {
  const result = useBenchlingQuery<CreateModalStudyQuery>(CREATE_MODAL_STUDIES_QUERY, {
    fetchPolicy: 'network-only',
    renderCachedDataWhenRefetching: true,
    variables: {id: props.studySchemaId},
  });
  // resultType is defined here to enable use of assertNever in the default case
  const resultType = result.type;
  switch (resultType) {
    case QueryResultType.PENDING:
      return (
        <Modal onRequestClose={props.onRequestClose} title="Create study">
          <CenteredHorizontalThrobber />
        </Modal>
      );
    case QueryResultType.ERROR: {
      return (
        <Modal onRequestClose={props.onRequestClose} title="Create study">
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text="An error occurred while loading this study schema. Refresh the page and try again."
          />
        </Modal>
      );
    }
    case QueryResultType.SUCCESS: {
      if (isInaccessible(result.data.studySchema)) {
        return (
          <Modal onRequestClose={props.onRequestClose} title="Create study">
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text="This study schema is not accessible. Refresh the page and try again."
            />
          </Modal>
        );
      }
      const studySchema = result.data.studySchema;
      return <CreateStudyModal {...props} studySchema={studySchema} />;
    }
    default: {
      assert(false, `Unexpected QueryResultType: ${assertNever(resultType)}`);
    }
  }
}

function CreateStudyModal(props: CreateStudyModalProps): JSX.Element {
  // Allows the modal to be open and hidden in the background. Used for re-opening the modal with the same
  // state when non-blocking creation fails
  const [isBackground, setIsBackground] = useState(false);

  const studyType = props.studySchema.studyType;
  const adapter = getTypedStudyAdapter(studyType);
  const typedStudyState = adapter.useTypedStudyState(props.studySchema, props.initialValues);
  const [currentStepKey, setCurrentStepKey] = useState(adapter.getSteps()[0]?.key);
  // when a name template is disabled, only the nameTemplateParts are removed from nameTemplates[0]
  const studyNameTemplates = props.studySchema.nameTemplates.filter(
    (template) => template.templateType === NameTemplateType.STUDY
  );
  assert(
    studyNameTemplates.length <= 1,
    'Expected at most one study name template present in the study schema.'
  );
  const hasNameTemplate =
    studyNameTemplates.length > 0 ? studyNameTemplates[0].nameTemplateParts.length > 0 : false;

  const procedureRunNameTemplates = props.studySchema.nameTemplates.filter(
    (template) => template.templateType === NameTemplateType.PROCEDURE_RUN
  );
  assert(
    procedureRunNameTemplates.length <= 1,
    'Expected at most one procedure run name template in the study schema.'
  );

  const schema = useMemo(
    () =>
      ({
        id: props.studySchema.id,
        name: props.studySchema.name,
        fields: props.studySchema.fieldsJson as Array<SchemaField>,
        prefix: props.studySchema.prefix,
        nameTemplates: studyNameTemplates,
      }) satisfies DraftStudySchema,
    [props.studySchema, studyNameTemplates]
  );
  const draftData = useDraftStudy(schema, props.initialValues, adapter, typedStudyState);
  const {studyParams} = draftData;
  const estimateResult = useTimingEstimate<BulkCreateStudiesParam>(
    '/1/api/studies:estimate',
    studyParams && adapter.creation.async ? {creates: [studyParams]} : null
  );
  // Explicitly check if canChangeStudyableAssignment is false.
  // A null value means a folder was not selected
  const isChangeStudyableAssignmentDisabled = draftData.validation.canChangeStudyableAssignment === false;

  const handleSubmit =
    studyParams && shouldAllowSubmit(adapter.getSteps(), currentStepKey)
      ? (): void => {
          onCreateStudy(
            [studyParams],
            props.actionOrigin,
            adapter.type,
            adapter.getAnalyticsMetadata(typedStudyState),
            adapter.getPerformanceMetadata(typedStudyState),
            {
              onSuccess(async: boolean) {
                props.onRequestSubmit?.();
                refreshStudyFolder(studyParams.folderId);
                if (!async) {
                  // Show a success toast if creation was synchronous - avoid duplicate success toast if
                  // creation was asynchronous (CreationTrayTask indicates success in that case).
                  const notification = (
                    <>
                      Successfully created study <strong>{studyParams.name}</strong>.
                    </>
                  );
                  notifications.success(notification);
                }
              },
              onOpen: (response) => {
                onOpenStudyAfterCreate(response);
                // If the study is multi-experiment and an initial procedure is specified, open the
                // Add Experiment modal with that procedure pre-selected.
                if (adapter.isMultiProcedureRunEnabled && props.initialValues.initialProcedureId != null) {
                  navigateToStudyOverviewTab(response.createdIds[0]);
                  if (BenchlingConfig.STUDIES_ENABLE_PROCEDURE_RUN_DESIGN) {
                    legacyRunModal(AddProcedureRunPlanModal, {
                      initialProcedureId: props.initialValues.initialProcedureId,
                      studyId: response.createdIds[0],
                      studyType,
                    });
                  } else {
                    legacyRunModal(AddProcedureRunModal, {
                      initialProcedureId: props.initialValues.initialProcedureId,
                      studyId: response.createdIds[0],
                      studyType,
                      hasExistingUnitOpsToLink: false, // There should be no preexisting unit ops to link to upon study creation.
                    });
                  }
                }
              },
              onBackground(isBackground: boolean) {
                setIsBackground(isBackground);
              },
              onUnknownError: STUDY_UNKNOWN_ERROR_ACTION,
            }
          );
        }
      : null;

  if (isBackground) {
    return <></>;
  }

  const handleClose = (): void => {
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, ANALYTICS_MODULE.STUDIES, {
      actionOrigin: props.actionOrigin,
      studyType: adapter.type,
      ...adapter.getAnalyticsMetadata(typedStudyState),
    });
    props.onRequestClose();
  };

  return (
    <Modal
      buttons={
        adapter.getButtonsByStep(
          currentStepKey,
          setCurrentStepKey,
          !handleSubmit,
          draftData.validation.isStudyMetadataValid,
          typedStudyState
        ) || [
          {
            cancel: true,
            disabled: false,
            text: 'Cancel',
            dataTestAttrs: {component: 'CreateStudyModal', key: ModalButtonKey.CANCEL},
          },
          {disabled: !handleSubmit, submit: true, text: 'Create', style: ButtonStyle.SECONDARY},
        ]
      }
      title={
        <div
          className={css(styles.titleWrapper)}
          {...getTestProps({component: 'CreateStudyModal', element: 'title'})}
        >
          <span>Create study: {schema.name}</span>
          {adapter.getSteps().length > 0 && (
            <StepNav<string>
              steps={adapter.getSteps()}
              statusByStep={{}}
              currentStepKey={currentStepKey}
              onUpdateCurrentStep={setCurrentStepKey}
              variant={StepNavVariant.FLAT}
            />
          )}
        </div>
      }
      onRequestClose={handleClose}
      onRequestSubmit={handleSubmit}
      {...adapter.modalOptions(currentStepKey, studyType)}
      closeableOnEscapeKey={false}
      closeableOnBackgroundClick={false}
    >
      {(adapter.getSteps().length === 0 || currentStepKey === adapter.getSteps()[0].key) && (
        // Display basic study information inputs if there are no steps or only on the first step
        <>
          <Row>
            <Col sm={7}>
              <FormGroup disableBottomMargin={isChangeStudyableAssignmentDisabled}>
                <TextField
                  dataTestAttrs={{component: 'CreateStudyModal', element: 'nameInput'}}
                  label="Study name"
                  placeholder="New study name"
                  // if a naming template is set on the study schema, the user does not need to input a name
                  isRequired={!hasNameTemplate}
                  disabled={hasNameTemplate}
                  value={hasNameTemplate ? AUTOGENERATED_NAME_TEXT : draftData.inputs.nameFormInput.value}
                  autoFocus={true}
                  onChange={draftData.inputs.nameFormInput.onChange}
                  hasError={!draftData.validation.isNameValid}
                  error={
                    !draftData.validation.isNameValid
                      ? {
                          message: `Study name must be less than or equal to ${getStudyMaxNameLength(
                            studyType
                          )} characters.`,
                          style: InputErrorMessageStyle.ERROR,
                        }
                      : undefined
                  }
                />
              </FormGroup>
            </Col>
            <Col sm={5}>
              <FormGroup disableBottomMargin={isChangeStudyableAssignmentDisabled}>
                <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
                <FolderSelector
                  dataTestAttrs={{component: 'CreateStudyModal', element: 'studyFolderSelector'}}
                  enableRedesign={true}
                  showInventory={false}
                  showNotebook={true}
                  unsetOnFetchFail={false}
                  {...draftData.inputs.folderInput}
                />
              </FormGroup>
            </Col>
          </Row>
          {isChangeStudyableAssignmentDisabled && (
            <Row>
              <Col sm={12}>
                <ChangeStudyableAssignmentPermissionWarningBanner
                  warningOriginType={ChangeStudyableAssignmentPermissionOriginType.FOLDER}
                />
              </Col>
            </Row>
          )}
          {schema.prefix === EMPTY_PREFIX && (
            <Row>
              <Col sm={7}>
                <FormGroup>
                  <TextField
                    label="ID"
                    placeholder=""
                    isRequired={true}
                    autoFocus={false}
                    {...draftData.inputs.displayIdFormInput}
                    hasError={!draftData.validation.isDisplayIdValid}
                    error={
                      !draftData.validation.isDisplayIdValid
                        ? {
                            message: `Study ID must be less than or equal to ${STUDY_DISPLAY_ID_MAX_LENGTH} characters.`,
                            style: InputErrorMessageStyle.ERROR,
                          }
                        : undefined
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
          )}
          <Row>
            <Col sm={12}>
              <FormGroup>
                <TextField
                  label="Description"
                  placeholder="New study description"
                  isRequired={false}
                  autoFocus={false}
                  {...draftData.inputs.descriptionFormInput}
                  hasError={!draftData.validation.isDescriptionValid}
                  error={
                    !draftData.validation.isDescriptionValid
                      ? {
                          message: `Study description must be less than or equal to ${STUDY_DESCRIPTION_MAX_LENGTH} characters.`,
                          style: InputErrorMessageStyle.ERROR,
                        }
                      : undefined
                  }
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <SchemaTagInputsWithDefault
                labelStyle={LabelStyle.LABEL}
                {...draftData.inputs.tagsInput}
                schema={schema}
              />
            </Col>
          </Row>
          {adapter.renderMetadataSection(typedStudyState)}
        </>
      )}
      {adapter.getSteps().length > 0 && (
        <>
          {adapter.renderStep(
            currentStepKey,
            typedStudyState,
            estimateResult,
            props.onRequestClose,
            studyType
          )}
        </>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  titleWrapper: {display: 'flex', flexWrap: 'wrap', columnGap: '50px', rowGap: 10},
});
